import { Map, List, fromJS } from 'immutable';
import { listToMap, keyIn, ISO_8601_MS_RESOLUTION } from 'flow/utils';
import {
    compareChatMessageAge,
    dropMessagesOlderThan,
    mergeNewChatMessageList,
    pageLeavesAGap,
} from 'flow/utils/message-utils';
import { addGeneralLogEntry } from 'flow/utils/browser-utils';
import moment from 'moment';
import { ChatContent } from 'flow/utils/message-utils';
import Actions from 'flow/actions';

const initialChatsState = Map({
    messages: Map(), // Map chat message history [ChatSessionEntityKey:[ChatMessageDTO]]
    reactions: Map(), // Map reactions by session and message key [ChatSessionKey:{ChatMessageKey:List<Reaction>}]
    composer: Map(),
    userToSession: Map(), // Map chatuserkey -> to chatsessionkey
    sessions: Map(),
    publicSessions: List(),
    client: undefined,
    previewImage: null,
    details: Map(), // Map chat message detail history [ChatSessionEntityKey:[ChatMessageDTO]]
    extensions: Map(),
    failedMessages: Map(),
    selectedThreadMessage: Map(),
    attachmentImageLoading: false,
});

/**
 * Extract reactions from a list of messages into the top-level reactions map.
 * Returns the updated state with reactions stored at reactions[sessionKey][messageKey].
 *
 * An absent `reactions` field and an empty one mean different things. A fetched
 * message always carries the field, so empty is the server saying there are
 * none - a reaction removed while we were away arrives only as that, never as a
 * removal, so the stored entry has to go or it outlives the reaction. A message
 * pushed over Top carries no `reactions` field at all, which says nothing about
 * them; deleting on that would drop reactions we correctly hold.
 */
const extractReactions = (state, sessionKey, messages) => {
    let reactions = state.getIn(['reactions', sessionKey], Map());
    messages.forEach((message) => {
        const messageReactions = message.get('reactions');
        if (!messageReactions) return;

        reactions =
            messageReactions.size > 0
                ? reactions.set(message.get('key'), messageReactions)
                : reactions.delete(message.get('key'));
    });
    return state.setIn(['reactions', sessionKey], reactions);
};

/** Whether this client holds the message a reaction is for, in the chat's own
 *  list or in one of its threads. */
const messageIsHeld = (state, { chatSessionKey, messageKey }) => {
    const inChat = state
        .getIn(['messages', chatSessionKey], List())
        .some((message) => message.get('key') === messageKey);
    if (inChat) return true;

    return state
        .getIn(['threadMessage', 'threadMessages', chatSessionKey], Map())
        .some((thread) =>
            thread.some((message) => message.get('key') === messageKey),
        );
};

/**
 * Apply a reaction create/delete to the top-level reactions map.
 */
const applyReaction = (state, action) => {
    const path = ['reactions', action.chatSessionKey, action.messageKey];

    // Nothing prunes this map, so a reaction for a message this client has
    // never seen would sit in it for the rest of the session with no row to
    // render it. The thread path used to refuse those; now that both paths
    // come through here, this is where that belongs.
    if (!messageIsHeld(state, action)) return state;

    const reactions = state.getIn(path, List());
    const index = reactions.findIndex(
        (r) => r.get('type') === action.data.type,
    );

    if (index > -1) {
        if (!action.data?.count) {
            return state.deleteIn([...path, index]);
        }
        return state.setIn([...path, index], fromJS(action.data));
    }

    if (action.type === Actions.DELETE_CHAT_REACTION) return state;

    return state.updateIn(path, List(), (prev) =>
        prev.push(fromJS(action.data)),
    );
};

export const updateListWithNewMessage = (messageList = List(), newMessage) => {
    const index = messageList.findIndex(
        (m) => m.get('key') === newMessage.get('key'),
    );

    if (index === -1) {
        return messageList.push(newMessage);
    } else {
        return messageList.update(index, (prev) => {
            let lastMessage = prev.mergeDeep(newMessage);
            /*
             * Keep a post message as post, getSessions does not present post type on messages.
             * Could result in latest post is rendered as a message.
             */
            if (prev.has('type') && prev.get('type') === 'post') {
                lastMessage = lastMessage.set('type', 'post');
            }
            return lastMessage;
        });
    }
};

const filterSessionSortInfo = (session) => {
    const latestActivity =
        session.getIn(['latestMessage', 'sent']) || session.get('timeCreated');
    const shouldUpdateArchived = session.has('chatArchived');
    const archived = session.getIn(['chatArchived', 'archived']) || null;

    return session
        .filter(keyIn('description'))
        .update('latestActivity', (prev) => latestActivity || prev)
        .update('archived', (prev) => (shouldUpdateArchived ? archived : prev))
        .update('roomType', (prev) => session.get('roomType') || prev)
        .update('members', (prev) => session.get('members') || prev);
};

const filterSessionsSortInfo = (sessions) => {
    return sessions.map(filterSessionSortInfo);
};

// eslint-disable-next-line complexity
export const chatsReducer = (state = initialChatsState, action) => {
    switch (action ? action.type : null) {
        case Actions.CLEAR_STATE:
            return initialChatsState;
        case Actions.RESULT_EXTENSIONS: {
            const extensionList = fromJS(action.data);

            const chatUsers = extensionList.reduce(
                (acc, i) => acc.set(i.get('chatUserKey'), i.get('key')),
                Map(),
            );

            return state.update('extensions', (prevChatUsers = Map()) =>
                prevChatUsers.merge(chatUsers),
            );
        }

        case Actions.RESULT_CHAT_SESSION_IMAGE: {
            delete action.data['numberOfActiveMembers'];
            action.data.roomImageResource = action.data.roomImageResource
                ? action.data.roomImageResource + '?' + +new Date()
                : action.data.roomImageResource; //todo find better way to make images update when url is unchanged
            return state.updateIn(['sessions', action.key], Map(), (prev) =>
                prev.mergeDeep(fromJS(action.data)),
            );
        }

        case Actions.RESULT_CHAT_SESSION_IMAGE_DELETED: {
            return state.setIn(
                ['sessions', action.key, 'roomImageResource'],
                null,
            );
        }
        case Actions.RESULT_CHAT_SESSION:
            delete action.data['numberOfActiveMembers'];
            return state
                .updateIn(['sessions', action.key], Map(), (prev) =>
                    prev.mergeDeep(fromJS(action.data)),
                )
                .updateIn(
                    ['sessionSortInfo', action.key],
                    (prevInfo = Map()) => {
                        return prevInfo.merge(
                            filterSessionSortInfo(fromJS(action.data)),
                        );
                    },
                );

        case Actions.RESULT_CHAT_SESSIONS: {
            action.data.forEach(
                (chatSession) => delete chatSession['numberOfActiveMembers'],
            );

            const mapped = listToMap(fromJS(action.data), (s) => s.get('key'));

            const sessionExternalFilter = (session) =>
                session.get('roomType') !== 'external';

            const filteredSessions = mapped.filter(sessionExternalFilter);

            return state
                .update('sessions', (prevSessions) =>
                    prevSessions.merge(filteredSessions),
                )
                .update('sessionSortInfo', (prevInfo = Map()) => {
                    return prevInfo.merge(
                        filterSessionsSortInfo(filteredSessions),
                    );
                });
        }
        case Actions.RESULT_PUBLIC_CHAT_SESSIONS: {
            const publicSessions = action.data;
            return state.update('publicSessions', () => fromJS(publicSessions));
        }
        case Actions.RESULT_CHAT_MESSAGES: {
            const newMessages = fromJS(action.data);

            return extractReactions(state, action.key, newMessages)
                .updateIn(['messages', action.key], (prevMessages = List()) => {
                    const merged = mergeNewChatMessageList(
                        prevMessages,
                        newMessages,
                        action.limitMessageAmount,
                    );
                    // A page that cannot be shown to continue the stored
                    // messages would merge into a list with a hole in the
                    // middle of it and nothing to show for it - and scrolling
                    // up asks for messages older than the oldest held, so it
                    // walks away from the hole rather than filling it. Cut the
                    // stranded messages loose instead: what is left starts at
                    // the page and reads continuously, and scrolling up brings
                    // the history back the ordinary way.
                    if (
                        pageLeavesAGap(prevMessages, newMessages, action.since)
                    ) {
                        return dropMessagesOlderThan(
                            merged,
                            newMessages.first(),
                        );
                    }

                    return merged;
                })
                .updateIn(['sessions', action.key], (session = Map()) => {
                    const latestMessageInSession = session.get('latestMessage');

                    // Get the message at the last index in the list of new messages, ie. the newest message
                    const latestMessageInNewMessages = newMessages.get(-1);

                    if (!latestMessageInNewMessages) {
                        addGeneralLogEntry(
                            `No messages received when fetching messages for session ${action.key}`,
                        );
                        return session;
                    }

                    /**
                     * About the if check below:
                     * If the latest message stored in the chat session is not the same message as the one just received,
                     * and the message just received is newer than the newest one in the chat session, we want to
                     * set the message just received as the latestMessage in the chat session.
                     */

                    if (
                        !latestMessageInSession ||
                        (latestMessageInNewMessages.get('key') !==
                            latestMessageInSession.get('key') &&
                            moment(
                                latestMessageInNewMessages.get('sent'),
                            ).isAfter(latestMessageInSession.get('sent')))
                    ) {
                        return session.set(
                            'latestMessage',
                            latestMessageInNewMessages,
                        );
                    } else {
                        return session;
                    }
                });
        }
        case Actions.RESULT_CHAT_MESSAGE_READ: {
            const readMessage = fromJS(action.data);

            const updateMessages = (messages) =>
                messages.map((m) => {
                    const compare = compareChatMessageAge(readMessage, m);
                    const hasBeenRead = compare >= 0;

                    return m.set('read', hasBeenRead);
                });

            return state
                .updateIn(['messages', action.key], List(), updateMessages)
                .updateIn(
                    ['sessions', action.key, 'numberOfUnreadMessages'],
                    () => 0,
                );
        }
        case Actions.RESULT_CHAT_THREAD_MESSAGE_READ: {
            const { chatSessionKey, parentThreadMessageKey } = action;

            const getMessageData = state
                .getIn(['messages', chatSessionKey])
                .map((message) => {
                    if (message.get('key') === parentThreadMessageKey) {
                        return message?.updateIn(
                            ['threadSummary', 'unreadCount'],
                            () => 0,
                        );
                    }

                    return message;
                });

            return state.updateIn(
                ['messages', chatSessionKey],
                () => getMessageData,
            );
        }
        case Actions.RESULT_CHAT_SESSION_UNREAD: {
            const chatSessionDTO = fromJS(action.data);

            const chatSessionKey = chatSessionDTO.get('key');
            const lastReadMessageKey = chatSessionDTO.get('lastReadMessage');
            const unreadMessages = chatSessionDTO.get('numberOfUnreadMessages');

            return state
                .setIn(
                    ['sessions', chatSessionKey, 'lastReadMessage'],
                    lastReadMessageKey,
                )
                .setIn(
                    ['sessions', chatSessionKey, 'numberOfUnreadMessages'],
                    unreadMessages,
                )
                .updateIn(
                    ['messages', chatSessionKey, -1],
                    (latestMessage) =>
                        latestMessage && latestMessage.set('read', false),
                ); // Set last message as unread
        }
        case Actions.RESULT_CHAT_MESSAGE: {
            const { data, isFetchDispatch, key, isAttachment } = action;
            const chatMessage = fromJS(data);

            const updateSession = (session) => {
                if (!session) {
                    session = Map({ key });
                }

                if (!isAttachment) {
                    session = session.set('latestMessage', chatMessage);
                }

                return session
                    .delete('chatArchived')
                    .update(
                        'numberOfUnreadMessages',
                        (count) =>
                            count +
                            (chatMessage.get('read', false) || isFetchDispatch
                                ? 0
                                : 1),
                    );
            };

            return extractReactions(state, key, List([chatMessage]))
                .updateIn(['messages', key], List(), (prev) =>
                    updateListWithNewMessage(prev, chatMessage),
                )
                .updateIn(['sessions', key], updateSession)
                .setIn(
                    ['sessionSortInfo', key, 'latestActivity'],
                    chatMessage.get('sent'),
                )
                .deleteIn(['sessionSortInfo', key, 'archived']);
        }

        case Actions.RESULT_FAILED_CHAT_MESSAGE: {
            const messageDTO = action.messageDTO;
            const messageText = messageDTO.message;
            const chatDate = action.dateTime;
            const ownExtensionKey = action.ownExtensionKey;
            const messageKey = action.messageKey;
            const attachment = messageDTO.attachment;

            const chatMessage = fromJS({
                key: 'failedchatmessage-' + messageKey,
                messageDTO: messageDTO,
                message: messageText,
                attachment: attachment,
                failed: true,
                sent: chatDate,
                from: { extensionKey: ownExtensionKey },
            });

            const updateListWithFailedMessage = (
                failedMessagesList = List(),
                newFailedMessage,
            ) => {
                return failedMessagesList.push(newFailedMessage);
            };

            return state.updateIn(
                ['failedMessages', action.key],
                List(),
                (prev) => updateListWithFailedMessage(prev, chatMessage),
            );
        }

        case Actions.RESULT_REMOVE_FAILED_MESSAGE: {
            const sessionKey = action.sessionKey;
            const messageKey = action.messageKey;

            return state.updateIn(['failedMessages', sessionKey], (session) =>
                session.filter((message) => message.get('key') !== messageKey),
            );
        }

        case Actions.RESULT_CHAT_POST: {
            const chatSessionKey = action.sessionKey;
            const chatPost = action.post;

            return state.updateIn(
                ['messages', chatSessionKey],
                (messageList = List()) =>
                    updateListWithNewMessage(messageList, chatPost),
            );
        }

        case Actions.RESULT_CHAT_COMMENT: {
            if (
                !state.hasIn(['messages', action.key]) &&
                !state.hasIn(['content', ChatContent.POSTS, action.key])
            ) {
                return state;
            }
            const addComment = (messages = List()) => {
                const messageIndex = messages.findIndex(
                    (m) => m.get('key') === action.chatMessageKey,
                );

                if (messageIndex === -1) {
                    return undefined;
                }

                const newComment = fromJS(action.data);
                const chatMessage = messages.get(messageIndex);

                const updatedMessage = chatMessage.update(
                    'chatComments',
                    List(),
                    (comments) => {
                        const commentIndex = comments.findIndex(
                            (c) => c.get('key') === newComment.get('key'),
                        );
                        if (commentIndex > -1) {
                            return comments.update(commentIndex, (comment) =>
                                comment.merge(newComment),
                            );
                        }
                        return comments.push(newComment);
                    },
                );

                return messages.set(messageIndex, updatedMessage);
            };

            return state
                .updateIn(['messages', action.key], addComment)
                .updateIn(
                    ['content', ChatContent.POSTS, action.key],
                    addComment,
                );
        }

        case Actions.RESULT_CHAT_COMMENTS: {
            if (
                !state.hasIn(['messages', action.key]) &&
                !state.hasIn(['content', ChatContent.POSTS, action.key])
            ) {
                return state;
            }
            const updateComments = (messages = List()) => {
                const index = messages.findIndex(
                    (m) => m.get('key') === action.chatMessageKey,
                );
                if (index === -1) {
                    return undefined;
                }
                const chatMessage = messages.get(index);

                const updatedComments = chatMessage.set(
                    'chatComments',
                    fromJS(action.data),
                );

                return messages.set(index, updatedComments);
            };

            return state
                .updateIn(['messages', action.key], updateComments)
                .updateIn(
                    ['content', ChatContent.POSTS, action.key],
                    updateComments,
                );
        }

        case Actions.RESULT_OLDER_CHAT_MESSAGES: {
            const olderMessages = fromJS(action.data);
            const keysAlreadyInState = state
                .getIn(['messages', action.key], List())
                .map((message) => message.get('key'));

            return extractReactions(state, action.key, olderMessages).updateIn(
                ['messages', action.key],
                List(),
                (session) => {
                    return olderMessages
                        .filter(
                            (message) =>
                                !keysAlreadyInState.includes(
                                    message.get('key'),
                                ),
                        )
                        .concat(session);
                },
            );
        }
        case Actions.RESULT_CHAT_POST_LIKE: {
            if (
                !state.hasIn(['messages', action.sessionKey]) &&
                !state.hasIn(['content', ChatContent.POSTS, action.sessionKey])
            ) {
                return state;
            }

            const addLikes = (messages = List()) => {
                const chatPostIndex = messages.findIndex(
                    (msg) => msg.get('key') === action.postKey,
                );

                if (chatPostIndex === -1) {
                    return undefined;
                }
                const chatMessage = messages.get(chatPostIndex);

                const updatedLikes = chatMessage.set(
                    'likedBy',
                    fromJS(action.data),
                );

                return messages.set(chatPostIndex, updatedLikes);
            };

            return state
                .updateIn(['messages', action.sessionKey], addLikes)
                .updateIn(
                    ['content', ChatContent.POSTS, action.sessionKey],
                    addLikes,
                );
        }
        case Actions.RESULT_ADD_CHAT_ADMIN:
            if (
                !state
                    .getIn(['sessions', action.key, 'admins'], List())
                    .includes(action.data)
            ) {
                return state.updateIn(
                    ['sessions', action.key, 'admins'],
                    List(),
                    (adminList) => adminList.push(action.data),
                );
            } else {
                return state;
            }
        case Actions.RESULT_REMOVE_CHAT_ADMIN:
            return state.updateIn(
                ['sessions', action.key, 'admins'],
                List(),
                (adminList) => adminList.filter((key) => key !== action.data),
            );
        case Actions.RESULT_CLIENT_REGISTER:
            return state.update('client', () => fromJS(action.data));

        case Actions.COMPOSER_UPDATE: {
            if (action?.threadKey) {
                return state.update('composer', (composer) =>
                    composer.updateIn(
                        [action.key, action.threadKey, 'text'],
                        () => action.text,
                    ),
                );
            } else {
                return state.update('composer', (composer) =>
                    composer.updateIn([action.key, 'text'], () => action.text),
                );
            }
        }
        case Actions.PUBLIC_MESSAGE_LOADING:
            return state.update('publicMessageLoading', () => action.status);

        case Actions.ATTACHMENT_UPDATE:
            return state.update('composer', (composer) => {
                const { key, threadMessageKey } = action;
                const updateArray = threadMessageKey
                    ? [key, threadMessageKey, 'attachment']
                    : [key, 'attachment'];
                return composer.updateIn(updateArray, () => action.attachment);
            });
        case Actions.RESULT_CHAT_LEAVE_ROOM:
            return state
                .deleteIn(['sessions', action.key])
                .deleteIn(['sessionSortInfo', action.key]);
        case Actions.RESULT_CHAT_REMOVE_CHAT_MEMBER: {
            return state.updateIn(
                ['sessions', action.key, 'members'],
                List(),
                (memberList) =>
                    memberList.filter(
                        (member) => member.get('key') !== action.chatUserKey,
                    ),
            );
        }
        case Actions.RESULT_CHAT_ADD_CHAT_MEMBER: {
            const newMember = {
                extensionKey: action.extensionKey,
                removed: false,
                key: action.chatUserKey,
            };
            return state.updateIn(
                ['sessions', action.key, 'members'],
                (prevMembers) =>
                    prevMembers.some(
                        (member) => member.get('key') === action.chatUserKey,
                    )
                        ? prevMembers
                        : prevMembers.push(fromJS(newMember)),
            );
        }
        case Actions.RESULT_CHAT_CREATE_SESSION:
            delete action.data['numberOfActiveMembers'];
            action.data.timeCreated = moment().format(ISO_8601_MS_RESOLUTION);
            return state
                .updateIn(['sessions', action.data.key], Map(), (prev) =>
                    prev.mergeDeep(fromJS(action.data)),
                )
                .updateIn(
                    ['userToSession', action.key],
                    Map(),
                    () => action.data.key,
                )
                .updateIn(
                    ['sessionSortInfo', action.data.key],
                    (prevInfo = Map()) => {
                        return prevInfo.merge(
                            filterSessionSortInfo(fromJS(action.data)),
                        );
                    },
                );
        case Actions.RESULT_CHAT_MUTE_SESSION:
            return state.updateIn(['sessions', action.key], (prev) => {
                // If the chat was unmuted, data is null. Remove the key entirely.
                return action.data
                    ? prev.set('chatMuted', fromJS(action.data))
                    : prev.delete('chatMuted');
            });
        case Actions.CHAT_PREVIEW_IMAGE: {
            return state.set('previewImage', action.data);
        }
        case Actions.RESULT_CHAT_ARCHIVE_SESSION: {
            delete action.data['numberOfActiveMembers'];
            const chatData = fromJS(action.data);
            const chatSessionKey = action.key;

            return state.setIn(['sessions', chatSessionKey], chatData);
        }
        case Actions.RESULT_CHAT_UNARCHIVE_SESSION: {
            delete action.data['numberOfActiveMembers'];
            const chatData = fromJS(action.data);
            const chatSessionKey = action.key;

            return state.setIn(['sessions', chatSessionKey], chatData);
        }
        case Actions.RESULT_CHAT_DETAILS: {
            const details = fromJS(action.data);
            const chatSessionKey = action.key;

            return state.updateIn(['details', chatSessionKey], Map(), (prev) =>
                prev.mergeDeep(details),
            );
        }
        case Actions.RESULT_CHAT_CONTENT: {
            const newContent = fromJS(action.data);
            const contentType = action.contentType;

            return state.updateIn(
                ['content', contentType, action.key],
                (prevContent = List()) => {
                    return mergeNewChatMessageList(
                        prevContent,
                        newContent,
                        false,
                    );
                },
            );
        }
        case Actions.RESULT_CHAT_CONTENT_HISTORY: {
            const newContent = fromJS(action.data);
            const contentType = action.contentType;

            const firstContentKey = state
                .getIn(['content', contentType, action.key], List())
                .first()
                .get('key');
            return state.updateIn(
                ['content', contentType, action.key],
                List(),
                (content) => {
                    return newContent
                        .takeUntil(
                            (content) => content.get('key') === firstContentKey,
                        )
                        .concat(content);
                },
            );
        }
        case Actions.GET_CHAT_REACTIONS: {
            const path = [
                'reactions',
                action.chatSessionKey,
                action.messageKey,
            ];
            const reactions = fromJS(action.data);

            // Empty means the server says there are none, so the entry goes
            // rather than being stored empty - same rule as extractReactions.
            return reactions?.size > 0
                ? state.setIn(path, reactions)
                : state.deleteIn(path);
        }

        case Actions.RESULT_CHAT_REACTION:
        case Actions.DELETE_CHAT_REACTION: {
            // Thread replies included: they render through the same component,
            // which reads the shared map, so a second copy on the message
            // object only meant the panel drew nothing.
            return applyReaction(state, action);
        }
        case Actions.ADD_QUOTED_MESSAGE: {
            return state.setIn(
                ['composer', action.payload.sessionKey, 'quotedMessage'],
                action.payload.message,
            );
        }
        case Actions.REMOVE_QUOTED_MESSAGE: {
            return state.deleteIn([
                'composer',
                action.payload,
                'quotedMessage',
            ]);
        }
        case Actions.OPEN_SELECTED_THREAD_MESSAGE: {
            const { chatSessionKey, chatMessageKey } = action;
            return state.setIn(
                ['threadMessage', 'currentThread'],
                fromJS({ chatSessionKey, chatMessageKey }),
            );
        }

        case Actions.CLEAR_SELECTED_THREAD_MESSAGE: {
            return state.deleteIn(['threadMessage', 'currentThread']);
        }

        case Actions.RECEIVE_THREAD_MESSAGES: {
            const { data, chatSessionKey, chatMessageKey } = action;

            return extractReactions(
                state,
                chatSessionKey,
                fromJS(data),
            ).updateIn(
                [
                    'threadMessage',
                    'threadMessages',
                    chatSessionKey,
                    chatMessageKey,
                ],
                (threadMessageList = List()) =>
                    mergeNewChatMessageList(
                        threadMessageList,
                        fromJS(data),
                        false,
                    ),
            );
        }

        case Actions.RECEIVE_THREAD_MESSAGE: {
            const { data, chatSessionKey, chatMessageKey, isAttachment } =
                action;

            if (isAttachment) {
                return state;
            }

            return extractReactions(
                state,
                chatSessionKey,
                List([fromJS(data)]),
            ).updateIn(
                [
                    'threadMessage',
                    'threadMessages',
                    chatSessionKey,
                    chatMessageKey,
                ],
                (messageList = List()) =>
                    updateListWithNewMessage(messageList, fromJS(data)),
            );
        }

        case Actions.ATTACHMENT_IMAGE_LOADED: {
            return state.updateIn(['attachmentImageLoading'], (prev) => !prev);
        }

        default:
            return state;
    }
};

export default chatsReducer;
